// vMix preset (.vmix) builder.
//
import { IN_MATCH_SLUG, IN_MATCH_SWAPPED_SUFFIX, swappedOverlayUrl } from './obsExport.js';

// vMix presets are full app-state XML — root <XML>, then <Input>, <State>,
// <RecordingSettings>, <OutputFormat>, etc. vMix's OpenPreset NPEs if those
// boilerplate sections are missing, so building a preset from scratch is
// brittle. Instead we keep a known-good template at public/vmix-template.vmix
// (one Browser input + one Blank input + all defaults) and clone the Browser
// input N times — one per granted overlay — with fresh GUIDs and the right
// title + URL.
//
// Browser input shape inside the template:
//   <Input Type="5000" ... Key="..." OriginalTitle="..." Title="...">URL</Input>
// The URL lives as the element's inner text, not an attribute.

const TEMPLATE_URL = '/vmix-template.vmix';

const newGuid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}-${Math.random().toString(16).slice(2, 6)}-${Math.random().toString(16).slice(2, 6)}-${Math.random().toString(16).slice(2, 14)}`;

const escapeAttr = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

// Inner text only needs &, <, > escaped — quotes are fine.
const escapeText = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

function cloneBrowserInput(browserBlock, { name, url }) {
  const title = escapeAttr(name);
  return browserBlock
    .replace(/Key="[^"]*"/, `Key="${newGuid()}"`)
    .replace(/\bOriginalTitle="[^"]*"/, `OriginalTitle="${title}"`)
    .replace(/\bTitle="[^"]*"/, `Title="${title}"`)
    .replace(/>[^<]*<\/Input>/, `>${escapeText(url)}</Input>`);
}

export async function buildVmixPreset({ overlays }) {
  if (!overlays?.length) {
    throw new Error('No overlays to export.');
  }
  const res = await fetch(TEMPLATE_URL);
  if (!res.ok) {
    throw new Error(`Could not load vMix template (HTTP ${res.status}).`);
  }
  const template = await res.text();

  const browserMatch = template.match(/<Input Type="5000"[\s\S]*?<\/Input>/);
  if (!browserMatch) {
    throw new Error("Couldn't locate Browser input in vMix template.");
  }
  const browserBlock = browserMatch[0];

  // vMix has no in-input visibility toggle worth scripting here, so the
  // swapped in-match page ships as its own input the operator cuts to.
  const expanded = overlays.flatMap((o) =>
    o.slug === IN_MATCH_SLUG
      ? [o, { ...o, name: `${o.name}${IN_MATCH_SWAPPED_SUFFIX}`, url: swappedOverlayUrl(o.url) }]
      : [o],
  );
  const newBlocks = expanded.map((o) => cloneBrowserInput(browserBlock, o)).join('\n  ');
  return template.replace(browserBlock, newBlocks);
}

export async function downloadVmixPreset({ packageSlug, overlays }) {
  const xml = await buildVmixPreset({ overlays });
  // octet-stream rather than application/xml so browser extensions that
  // monkey-patch Blob for XML MIMEs (and try to re-parse the content) don't
  // intercept and reject the download with an insertAdjacentHTML error.
  const blob = new Blob([xml], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${packageSlug}-vmix-preset.vmix`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
